import { showAlertCallout } from "@/shared/alerts";
import { useControlResultRepository } from "@/shared/contexts/repository-context";
import { alertCalloutPromise } from "@/shared/utils/alert-callout-promise";
import { getGetControlResultListPaginatedQueryKey } from "@/spz-api/control-results/control-results";
import type { GetControlResultListPaginatedResponseDto } from "@/spz-api/model";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useBulkActionStore } from "../store/bulk-action.store";
import { useBulkSelectionStore } from "../store/bulk-selection.store";
import { useControlResultFiltersStore } from "../store/control-result-filters.store";
import { parseTransitions } from "../utils/workflow-parsers";
import { filterWorkflowTransitions } from "../utils/workflow-transitions-builder";
import { useUploadCommentAttachmentMutation } from "./mutations/use-upload-comment-attachment.mutation";

interface UseControlResultsBulkWorkflowProps {
  controlResults: GetControlResultListPaginatedResponseDto | undefined;
  workflowPermissions: {
    canStartInvestigation: boolean;
    canSkipResult: boolean;
    canCompleteInvestigation: boolean;
  };
  selectedCount: number;
  filtersJson: string;
  controlId: string;
}

export interface SelectionAnalysis {
  isHomogeneous: boolean;
  currentStepKeyName: string | null;
}

export function useControlResultsBulkWorkflow({
  controlResults,
  workflowPermissions,
  selectedCount,
  filtersJson,
  controlId,
}: UseControlResultsBulkWorkflowProps) {
  const { selectedRowIds, selectAllAcrossPages, deselectedRowIds } =
    useBulkSelectionStore();
  const { columnFilters } = useControlResultFiltersStore();
  const { t } = useTranslation();
  const { setIsBulkActionInProgress } = useBulkActionStore();
  const controlResultRepository = useControlResultRepository();
  const queryClient = useQueryClient();

  const bulkUpdateWorkflowMutation = useMutation({
    mutationFn: async ({
      transitionId,
      formData,
    }: {
      transitionId: string;
      formData: Record<string, unknown> | null;
    }) => {
      await controlResultRepository.bulkUpdateWorkflowStep({
        controlId,
        transitionId: transitionId,
        formData: formData,
        ...(selectAllAcrossPages
          ? { filters: filtersJson, resultIds: Array.from(deselectedRowIds) }
          : { resultIds: Array.from(selectedRowIds) }),
      });
    },
    onSettled: () => {
      setIsBulkActionInProgress(false);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getGetControlResultListPaginatedQueryKey(controlId),
      });
    },
  });
  const statusFilterValue = columnFilters.find(
    (f) => f.id === "workflow_step_uuid",
  )?.value;
  const selectionAnalysis = useMemo((): SelectionAnalysis => {
    if (
      !controlResults ||
      (selectedRowIds.size === 0 && !selectAllAcrossPages)
    ) {
      return {
        isHomogeneous: false,
        currentStepKeyName: null,
      };
    }

    if (selectAllAcrossPages) {
      if (!statusFilterValue || !Array.isArray(statusFilterValue)) {
        return {
          isHomogeneous: false,
          currentStepKeyName: null,
        };
      }

      if (statusFilterValue.length > 1) {
        return {
          isHomogeneous: false,
          currentStepKeyName: null,
        };
      }

      if (statusFilterValue.length === 1) {
        const step = controlResults.relationships.workflowSteps.find(
          (s) => s.id === statusFilterValue[0],
        );
        return {
          isHomogeneous: true,
          currentStepKeyName: step?.key_name || null,
        };
      }

      return {
        isHomogeneous: false,
        currentStepKeyName: null,
      };
    }

    const selectedIds = Array.from(selectedRowIds);
    const selectedRows = controlResults.data.filter((row) =>
      selectedIds.includes(row.id),
    );

    if (selectedRows.length === 0) {
      return {
        isHomogeneous: false,
        currentStepKeyName: null,
      };
    }

    const uniqueStepIds = new Set(
      selectedRows.map((row) => row.currentWorkflowStepId).filter(Boolean),
    );

    const isHomogeneous = uniqueStepIds.size === 1;

    let currentStepKeyName: string | null = null;
    if (isHomogeneous && selectedRows.length > 0) {
      const stepId = selectedRows[0].currentWorkflowStepId;
      const step = controlResults.relationships.workflowSteps.find(
        (s) => s.id === stepId,
      );
      currentStepKeyName = step?.key_name || null;
    }

    return { isHomogeneous, currentStepKeyName };
  }, [selectedRowIds, controlResults, selectAllAcrossPages, statusFilterValue]);

  const filteredTransitions = useMemo(() => {
    if (!selectionAnalysis.isHomogeneous || !controlResults) {
      return null;
    }

    const currentStep = controlResults.relationships.workflowSteps.find(
      (step) => step.key_name === selectionAnalysis.currentStepKeyName,
    );

    const transitions = parseTransitions(currentStep?.transitions || null);

    return filterWorkflowTransitions(transitions, {
      canStartInvestigationAndReinclude:
        workflowPermissions.canStartInvestigation,
      canSkipResult: workflowPermissions.canSkipResult,
      canCompleteInvestigation: workflowPermissions.canCompleteInvestigation,
    });
  }, [selectionAnalysis, controlResults, workflowPermissions]);

  const uploadCommentAttachmentMutation = useUploadCommentAttachmentMutation({
    controlId,
    controlResultId: Array.from(selectedRowIds)[0] ?? "",
  });

  const handleTransitionSubmit = useCallback(
    async (transitionId: string, formData: Record<string, unknown> | null) => {
      setIsBulkActionInProgress(true);
      try {
        const rawData =
          formData && typeof formData === "object" ? formData : null;

        const possibleAttachment = rawData?.attachment as
          | File
          | undefined
          | null;

        let attachmentId: string | undefined;
        if (possibleAttachment instanceof File) {
          const result = await uploadCommentAttachmentMutation.mutateAsync({
            file: possibleAttachment,
          });

          attachmentId = result?.id;
        }

        const { attachment, ...rest } = (rawData ?? {}) as Record<
          string,
          unknown
        >;
        const safeData: Record<string, unknown> | null = {
          ...rest,
          ...(attachmentId ? { attachment: attachmentId } : {}),
        };
        await alertCalloutPromise(
          bulkUpdateWorkflowMutation.mutateAsync({
            transitionId,
            formData: Object.keys(safeData).length ? safeData : {},
          }),
          {
            loading: {
              title: t("bulk_transition_in_progress", {
                count: selectedCount,
              }),
            },
            success: {
              title: t("bulk_update_success", {
                count: selectedCount,
              }),
            },
            error: {
              title: t("bulk_update_error"),
            },
          },
        );
      } catch (error) {
        showAlertCallout({
          type: "critical",
          title: t("generic_error"),
        });
      }
    },
    [
      bulkUpdateWorkflowMutation,
      selectedCount,
      t,
      setIsBulkActionInProgress,
      uploadCommentAttachmentMutation,
    ],
  );

  return {
    selectionAnalysis,
    filteredTransitions,
    handleTransitionSubmit,
  };
}
